import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Skeleton,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  TextField,
  Grid,
  Chip,
  Alert
} from '@mui/material';
import {
  DesktopMac as DesktopIcon,
  Tablet as TabletIcon,
  PhoneIphone as PhoneIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import api from '../../services/api';

// Device presets for preview
const devicePresets = {
  desktop: {
    name: 'Desktop',
    width: '100%',
    height: '100%',
    scale: 1,
    frame: false
  },
  tablet: {
    name: 'iPad',
    width: 768,
    height: 1024,
    scale: 0.75,
    frame: true
  },
  mobile: {
    name: 'iPhone',
    width: 375,
    height: 667,
    scale: 0.5,
    frame: true
  }
};

// Device frame component
const DeviceFrame = ({ device, children }) => {
  if (!devicePresets[device].frame) {
    return children;
  }

  return (
    <Box sx={{
      position: 'relative',
      backgroundColor: '#2a2a2a',
      borderRadius: device === 'tablet' ? '20px' : '30px',
      padding: device === 'tablet' ? '40px 20px' : '60px 15px 80px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: device === 'tablet' ? '15px' : '25px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: device === 'tablet' ? '40px' : '60px',
        height: '4px',
        backgroundColor: '#555',
        borderRadius: '2px'
      },
      '&::after': device === 'mobile' ? {
        content: '""',
        position: 'absolute',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '40px',
        height: '40px',
        backgroundColor: '#444',
        borderRadius: '50%',
        border: '2px solid #666'
      } : {}
    }}>
      {children}
    </Box>
  );
};

// Template Preview Component
// eslint-disable-next-line max-lines-per-function
const TemplatePreview = ({
  open,
  onClose,
  template,
  variables = {},
  onVariableChange,
  showVariableEditor = true
}) => {
  const [device, setDevice] = useState('desktop');
  const [loading, setLoading] = useState(true);
  const [previewHtml, setPreviewHtml] = useState('');
  const [fullscreen, setFullscreen] = useState(false);
  const [localVariables, setLocalVariables] = useState(variables);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && template) {
      generatePreview();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template, localVariables]);

  const generatePreview = async () => {
    try {
      setLoading(true);
      setError(null);

      // Replace variables in template content
      let htmlContent = template.htmlContent || '';

      // Add template CSS for better preview
      const previewStyles = `
        <style>
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
            margin: 20px;
            line-height: 1.6;
            color: #333;
          }
          .template-variable {
            background: #fff3cd;
            border: 1px solid #ffc107;
            padding: 2px 6px;
            border-radius: 4px;
            font-family: monospace;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 12px;
            text-align: left;
          }
          th {
            background-color: #f5f5f5;
            font-weight: bold;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none !important; }
          }
          @page {
            size: letter;
            margin: 1in;
          }
        </style>
      `;

      // Replace variables with values
      Object.entries(localVariables).forEach(([key, value]) => {
        const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');
        htmlContent = htmlContent.replace(regex, value || `<span class="template-variable">{{${key}}}</span>`);
      });

      // Also replace any @mentions with variable syntax
      htmlContent = htmlContent.replace(/@(\w+)/g, (match, varName) => {
        const value = localVariables[varName];
        return value || `<span class="template-variable">{{${varName}}}</span>`;
      });

      // Build complete HTML document
      const completeHtml = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${template.name || 'Template Preview'}</title>
  ${previewStyles}
  ${template.customStyles || ''}
</head>
<body>
  ${template.headerContent ? `<header>${template.headerContent}</header>` : ''}
  <main>
    ${htmlContent}
  </main>
  ${template.footerContent ? `<footer>${template.footerContent}</footer>` : ''}
</body>
</html>`;

      setPreviewHtml(completeHtml);
    } catch (err) {
      console.error('Preview generation failed:', err);
      setError('Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handleVariableChange = (varName, value) => {
    const newVariables = { ...localVariables, [varName]: value };
    setLocalVariables(newVariables);
    if (onVariableChange) {
      onVariableChange(newVariables);
    }
  };

  const handlePrint = () => {
    const iframe = document.getElementById('template-preview-iframe');
    if (iframe) {
      iframe.contentWindow.print();
    }
  };

  const handleDownload = async () => {
    try {
      // This would call the export_to_pdf endpoint
      const response = await api.exportToPDF({
        templateId: template.id,
        variables: localVariables
      });

      // Handle PDF download
      if (response.downloadUrl) {
        window.open(response.downloadUrl, '_blank');
      }
    } catch (err) {
      console.error('PDF export failed:', err);
      setError('Failed to export PDF');
    }
  };

  const deviceSettings = devicePresets[device];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={fullscreen ? false : 'lg'}
      fullWidth
      fullScreen={fullscreen}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Template Preview: {template?.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <ToggleButtonGroup
              value={device}
              exclusive
              onChange={(e, v) => v && setDevice(v)}
              size="small"
            >
              <ToggleButton value="desktop">
                <DesktopIcon sx={{ mr: 0.5 }} /> Desktop
              </ToggleButton>
              <ToggleButton value="tablet">
                <TabletIcon sx={{ mr: 0.5 }} /> Tablet
              </ToggleButton>
              <ToggleButton value="mobile">
                <PhoneIcon sx={{ mr: 0.5 }} /> Mobile
              </ToggleButton>
            </ToggleButtonGroup>

            <Divider orientation="vertical" flexItem />

            <Tooltip title="Refresh Preview">
              <IconButton onClick={generatePreview} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Print">
              <IconButton onClick={handlePrint} size="small">
                <PrintIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Download PDF">
              <IconButton onClick={handleDownload} size="small">
                <DownloadIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={fullscreen ? "Exit Fullscreen" : "Fullscreen"}>
              <IconButton onClick={() => setFullscreen(!fullscreen)} size="small">
                {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>

            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 2, height: fullscreen ? 'calc(100vh - 150px)' : '600px' }}>
          {/* Variable Editor Panel */}
          {showVariableEditor && template?.variables?.length > 0 && (
            <Paper sx={{ width: 300, p: 2, overflow: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom>
                Template Variables
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {template.variables.map((variable) => (
                  <Grid key={variable.name} size={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {variable.name}
                          <Chip
                            label={variable.type}
                            size="small"
                            sx={{ ml: 1, height: 16 }}
                          />
                        </Box>
                      }
                      value={localVariables[variable.name] || ''}
                      onChange={(e) => handleVariableChange(variable.name, e.target.value)}
                      placeholder={variable.defaultValue || ''}
                      helperText={variable.description}
                      required={variable.required}
                      type={
                        variable.type === 'number' ? 'number' :
                        variable.type === 'date' ? 'date' :
                        'text'
                      }
                    />
                  </Grid>
                ))}
              </Grid>

              <Button
                fullWidth
                variant="outlined"
                sx={{ mt: 2 }}
                onClick={() => setLocalVariables({})}
              >
                Clear All Variables
              </Button>
            </Paper>
          )}

          {/* Preview Area */}
          <Box sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            overflow: 'auto',
            p: device === 'desktop' ? 0 : 3
          }}>
            {error ? (
              <Alert severity="error">{error}</Alert>
            ) : loading ? (
              <Skeleton
                variant="rectangular"
                width={deviceSettings.width}
                height={device === 'desktop' ? '100%' : deviceSettings.height}
              />
            ) : (
              <Box sx={{
                width: device === 'desktop' ? '100%' : deviceSettings.width,
                height: device === 'desktop' ? '100%' : deviceSettings.height,
                transform: device === 'desktop' ? 'none' : `scale(${deviceSettings.scale})`,
                transformOrigin: 'top center',
              }}>
                <DeviceFrame device={device}>
                  <Box sx={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'white',
                    overflow: 'hidden',
                    borderRadius: device === 'desktop' ? 0 : '8px'
                  }}>
                    <iframe
                      id="template-preview-iframe"
                      srcDoc={previewHtml}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        display: 'block'
                      }}
                      title="Template Preview"
                      sandbox="allow-same-origin allow-scripts"
                    />
                  </Box>
                </DeviceFrame>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      {!fullscreen && (
        <DialogActions>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Preview shows template with current variable values
            </Typography>
          </Box>
          <Button onClick={onClose}>Close</Button>
          <Button variant="contained" onClick={handleDownload}>
            Export PDF
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default TemplatePreview;